import ReactECharts from 'echarts-for-react';
import { useEffect, useRef } from 'react';
import './CompanyScoreChart.css';
import useElementDimension from '../../hooks/useElementDimension';

export interface CompanyScoreChartProps {
  symbol: string;
  name: string;
  earnings: number[];
  pricemomentum: number[];
  risk: number[];
  fundamental: number[];
  relativevaluation: number[];
}

const CompanyScoreChart = ({
  symbol,
  name,
  earnings,
  pricemomentum,
  risk,
  fundamental,
  relativevaluation,
}: CompanyScoreChartProps) => {
  const chartRef = useRef<any>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { width, height } = useElementDimension(wrapperRef);

  const symbolEarnings = earnings[0] < 0 ? '-' : earnings[0];
  const symbolPriceMomentum = pricemomentum[0] < 0 ? '-' : pricemomentum[0];
  const symbolRisk = risk[0] < 0 ? '-' : risk[0];
  const symbolFundamental = fundamental[0] < 0 ? '-' : fundamental[0];
  const symbolRelativeValuation =
    relativevaluation[0] < 0 ? '-' : relativevaluation[0];

  const sectorEarnings = earnings[1] < 0 ? '-' : earnings[1];
  const sectorPriceMomentum = pricemomentum[1] < 0 ? '-' : pricemomentum[1];
  const sectorRisk = risk[1] < 0 ? '-' : risk[1];
  const sectorFundamental = fundamental[1] < 0 ? '-' : fundamental[1];
  const sectorRelativeValuation =
    relativevaluation[1] < 0 ? '-' : relativevaluation[1];

  useEffect(() => {
    chartRef.current?.getEchartsInstance().resize();
  }, [width, height]);

  const labelScale = width > 0 ? Math.min(1, Math.max(0.7, width / 360)) : 1;

  const config: any = {
    tooltip: {
      show: true,
      trigger: 'item',
      position: function (
        point: number[],
        params: any,
        dom: HTMLElement,
        rect: any,
        size: any,
      ) {
        if (!rect) return point;

        const dataX = rect.x + rect.width / 2;
        const dataY = rect.y;
        const viewWidth = size.viewSize[0];
        const boxWidth = size.contentSize[0];

        let posX = dataX - boxWidth / 2;
        let posY = dataY + 20;

        if (posX < 0) posX = 0;
        if (posX + boxWidth > viewWidth) posX = viewWidth - boxWidth;

        return [posX, posY];
      },
      alwaysShowContent: false,
      renderMode: 'html',
      className: 'company-score-tooltip',
      backgroundColor: '#fff',
      borderWidth: 1,
      textStyle: {
        color: '#333',
        fontSize: 12,
        overflow: 'break',
      },
      formatter: (params: any) => {
        const values = params.value;
        const indicators = config.radar.indicator;

        const color = params.color;

        const lines = indicators.map((ind: any, i: number) => {
          const label = ind.name.split('|')[1].replace(/\n/g, ' ');
          return `<div class="company-score-tooltip-row"><span><span class="company-score-tooltip-label" style="color: ${color}"></span>&nbsp;${label}</span> <span class="company-score-tooltip-value">${values[i]}</span></div>`;
        });
        return `<div class="company-score-tooltip-inner" style="--arrow-color:${color}"><div class="company-score-tooltip-title">${params.name}</div>${lines.join('')}</div>`;
      },
    },
    legend: {
      data: [symbol, name],
      icon: 'rect',
      bottom: '5%',
      padding: [0, 0, 0, 0],
      textStyle: {
        fontSize: 12,
        color: '#000',
        verticalAlign: 'middle',
      },
      itemWidth: 12,
      itemHeight: 12,
    },
    radar: {
      center: ['50%', '50%'],
      radius: labelScale < 1 ? '45%' : '50%',
      indicator: [
        { name: `${symbolEarnings} / ${sectorEarnings}|\nEarnings`, max: 10 },
        {
          name: `${symbolPriceMomentum} / ${sectorPriceMomentum}|\nPrice\nMomentum`,
          max: 10,
        },
        { name: `${symbolRisk} / ${sectorRisk}|\nRisk`, max: 10 },
        {
          name: `${symbolRelativeValuation} / ${sectorRelativeValuation}|\nRelative\nValuation`,
          max: 10,
        },
        {
          name: `${symbolFundamental} / ${sectorFundamental}|\nFundamental`,
          max: 10,
        },
      ],
      axisName: {
        formatter: (name: string) => {
          const parts = name.split('|');
          if (parts.length === 2) {
            const valueParts = parts[0].split(' ');
            return `{gray|${valueParts[0]} ${valueParts[1]}} {blue|${valueParts[2]}}{lbl|${parts[1]}}`;
          }
          return name;
        },
        rich: {
          gray: {
            align: 'center',
            color: '#37474F',
            fontWeight: 'bold',
            fontSize: Math.round(16 * labelScale),
          },
          blue: {
            align: 'center',
            color: '#32ADE6',
            fontWeight: 'bold',
            fontSize: Math.round(16 * labelScale),
          },
          lbl: {
            align: 'center',
            color: '#000000',
            fontWeight: 400,
            fontSize: Math.round(14 * labelScale),
            lineHeight: Math.round(18 * labelScale),
            padding: [2, 0, 0, 0],
          },
        },
      },
      splitNumber: 1,
    },
    series: [
      {
        type: 'radar',
        hoverAnimation: false,
        data: [
          {
            value: [
              symbolEarnings,
              symbolPriceMomentum,
              symbolRisk,
              symbolRelativeValuation,
              symbolFundamental,
            ],
            name: symbol,
            emphasis: { disabled: true },
            itemStyle: {
              color: '#37474F',
            },
            areaStyle: {
              color: '#68809F4D',
            },
          },
          {
            value: [
              sectorEarnings,
              sectorPriceMomentum,
              sectorRisk,
              sectorRelativeValuation,
              sectorFundamental,
            ],
            name: name,
            emphasis: { disabled: true },
            itemStyle: {
              color: '#32ADE6',
            },
            areaStyle: {
              color: '#2F80ED4D',
            },
          },
        ],
      },
    ],
  };

  return (
    <div className="company-score-chart-container">
      <div className="company-score-chart-wrapper" ref={wrapperRef}>
        <ReactECharts
          ref={chartRef}
          option={config}
          style={{ width: '100%', height: height > 0 ? height : '100%' }}
        />
      </div>
    </div>
  );
};

export default CompanyScoreChart;
